"use client";

// "use client" hoja interactiva: link activo vía usePathname (navegación cliente) — imposible en server.
// 2026-09-07 reference-style Resources mega menu — websearch 2026 (NN/g: group
// choices in 2-D panels with type hierarchy; Raze 2026: ≤4 top-level items;
// Karlmission 2026: value-led labels, conversion one click away).
// Grouped panel + announcement card pointing ONLY to real shipped routes.
import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight, BookOpen, ChevronDown, Code2, History, Newspaper, type LucideProps } from "lucide-react";
import type { ComponentType } from "react";
import { Dropdown, DropdownContent, DropdownTrigger } from "@/components/ui/dropdown";
import { localePath, stripLocalePrefix, type TranslationKey } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";
type NavIcon = ComponentType<LucideProps>;
interface MenuEntry {
  href: string;
  titleKey: TranslationKey;
  icon: NavIcon;
}
const RESOURCE_LEARN: MenuEntry[] = [{
  href: "/blog",
  titleKey: "navBlog",
  icon: Newspaper
}, {
  href: "/guides",
  titleKey: "navGuides",
  icon: BookOpen
}, {
  href: "/docs",
  titleKey: "navDocs",
  icon: Code2
}, {
  href: "/changelog",
  titleKey: "navChangelog",
  icon: History
}];
function MegaMenuLink(t0) {
  const $ = _c(22);
  const {
    entry,
    pathname,
    onNavigate
  } = t0;
  const t = useT();
  const locale = useLocale();
  let t1;
  if ($[0] !== entry.href || $[1] !== locale) {
    t1 = localePath(locale, entry.href);
    $[0] = entry.href;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  const cleanPath = stripLocalePrefix(pathname);
  const cleanHref = stripLocalePrefix(entry.href);
  const active = cleanPath === cleanHref || cleanPath.startsWith(`${cleanHref}/`);
  const Icon = entry.icon;
  const t2 = active ? "page" : undefined;
  const t3 = active ? "border-[var(--border-strong)] bg-[var(--accent-soft)] text-foreground" : "border-[var(--border)] bg-[var(--background-elevated)] text-muted-foreground group-hover:border-[var(--border-strong)] group-hover:text-foreground group-hover:shadow-sm";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("inline-flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)] border transition-all duration-200 group-hover:scale-[1.04]", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== Icon) {
    t5 = <Icon className="size-4" strokeWidth={1.75} aria-hidden="true" />;
    $[5] = Icon;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== entry.titleKey || $[11] !== t) {
    t7 = t(entry.titleKey);
    $[10] = entry.titleKey;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <span className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-normal text-foreground">{t7}</span>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowRight className="size-3.5 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:text-foreground group-hover:opacity-100" aria-hidden="true" />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== href || $[17] !== onNavigate || $[18] !== t2 || $[19] !== t6 || $[20] !== t8) {
    t10 = <Link href={href} onClick={onNavigate} role="menuitem" aria-current={t2} className="group flex items-center gap-3 rounded-[var(--radius-lg)] px-3 py-2.5 transition-colors hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]">{t6}{t8}{t9}</Link>;
    $[16] = href;
    $[17] = onNavigate;
    $[18] = t2;
    $[19] = t6;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}

// Trigger-anchored popover: DropdownContent positions fixed under the trigger
// (see dropdown.tsx updateCoords); no centering override so the panel deploys
// from the nav item with a small offset + origin animation.

export function ResourcesMenu(t0) {
  const $ = _c(31);
  const {
    open,
    onOpenChange
  } = t0;
  const t = useT();
  const pathname = usePathname();
  let t1;
  if ($[0] !== onOpenChange) {
    t1 = () => onOpenChange(false);
    $[0] = onOpenChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const close = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("navResources");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = open && "rotate-180";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("size-3.5 transition-transform duration-200", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <ChevronDown className={t4} aria-hidden="true" />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t5) {
    t6 = <DropdownTrigger data-testid="header-control" className="hidden items-center gap-1 rounded-[var(--radius-button)] px-3 py-1.5 text-sm font-medium transition-colors md:inline-flex text-foreground-soft hover:text-foreground" id="nav-resources-button">{t2}{t5}</DropdownTrigger>;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("navResourcesLearn");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("navResourcesLearn");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <p className="truncate px-3 pb-1.5 pt-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground/80" title={t7}>{t8}</p>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== close || $[19] !== pathname) {
    t10 = RESOURCE_LEARN.map(entry => <MegaMenuLink key={entry.titleKey} entry={entry} pathname={pathname} onNavigate={close} />);
    $[18] = close;
    $[19] = pathname;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <div className="flex flex-col gap-0.5">{t10}</div>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <DropdownContent align="start" sideOffset={8} aria-labelledby="nav-resources-button" className="w-56 p-1.5">{t9}{t11}</DropdownContent>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== onOpenChange || $[27] !== open || $[28] !== t12 || $[29] !== t6) {
    t13 = <Dropdown open={open} onOpenChange={onOpenChange}>{t6}{t12}</Dropdown>;
    $[26] = onOpenChange;
    $[27] = open;
    $[28] = t12;
    $[29] = t6;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}